import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Icon,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_DIALOG_CONTENT, V4_ICON_BUTTON, V4_SMALL_SCREEN } from '../../theme/v4Compat';
import Alert from '@mui/material/Alert';
import { notifications } from '../../utils';
import { FarmMedicalSupplySelect } from '../../elements';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyringe } from '@fortawesome/free-solid-svg-icons';
import { VetsMedicalSupplyViewModel } from '../../models/vets';

export interface VeterinaryLocationVaccinationModalResult {
  farmSupplyId: string;
  repeatDays: number;
  dose: number;
}

interface VeterinaryLocationVaccinationModalProps {
  opened: boolean;
  isLoaded: boolean;
  farmSupplies: VetsMedicalSupplyViewModel[];
  onModalAction: (result: VeterinaryLocationVaccinationModalResult | null) => void;
}

const VeterinaryLocationVaccinationModal: React.FC<any> = ({
  opened,
  isLoaded,
  farmSupplies,
  onModalAction
}: VeterinaryLocationVaccinationModalProps) => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const [dose, setDose] = React.useState(0);
  const [repeatDays, setRepeatDays] = React.useState(0);
  const [farmSupplyId, setFarmSupplyId] = React.useState('');
  const [giltsConfirmed, setGiltsConfirmed] = React.useState(false);

  React.useEffect(() => {
    if (opened) {
      // Require the gilts-only confirmation to be made deliberately each time.
      setGiltsConfirmed(false);
    }
  }, [opened]);

  const closeDialog = (text: string | null) => {
    if (text == null) {
      onModalAction(null);
      return;
    }

    if (!validateForm()) {
      return;
    }

    onModalAction({ farmSupplyId, repeatDays, dose });
    setFarmSupplyId('');
    setDose(0);
    setRepeatDays(0);
  };

  const validateForm = (): boolean => {
    if (!farmSupplyId) {
      notifications.warning(t('f-val-farm-supply'));
      return false;
    }
    if (!giltsConfirmed) {
      notifications.warning(t('f-loc-vaccination-confirm-required'));
      return false;
    }

    return true;
  };

  const handleFarmSupplyChange = (value: string) => {
    setFarmSupplyId(value || '');
  };

  const submitDisabled = farmSupplyId === '' || dose === 0 || !giltsConfirmed;

  return (
    <Dialog
      id="vaccinationDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      sx={styles.dialog}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="form-dialog-title"
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box sx={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={t('f-com-cancel')} arrow>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => closeDialog(null)}
                    aria-label="close"
                    sx={V4_ICON_BUTTON}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="h6">{t('f-loc-vaccination-title')}</Typography>
              </Box>

              <Button
                onClick={() => closeDialog('ok')}
                id="saveVaccinationBtn"
                startIcon={<FontAwesomeIcon icon={faSyringe} />}
                color="inherit"
                disabled={submitDisabled}
              >
                {t('f-loc-vaccination-action')}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="locationVaccinationDialogTitle">{t('f-loc-vaccination-title')}</DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Grid container direction="column" spacing={2} sx={styles.updateForm}>
          <Alert sx={styles.mt2} severity="info">
            {t('f-loc-vaccination-alert')}
          </Alert>

          {isLoaded && (
            <>
              <Grid item xs={12}>
                <FarmMedicalSupplySelect
                  supplies={farmSupplies}
                  value={farmSupplyId}
                  fullWidth
                  onSupplyChange={handleFarmSupplyChange}
                />
              </Grid>

              <TextField
                id="days-count-input"
                label={t('f-loc-feed-days')}
                variant="outlined"
                sx={styles.formControl}
                value={repeatDays}
                size="small"
                type="number"
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setRepeatDays(newValue);
                  }
                }}
              />

              <TextField
                id="dose-input"
                label={t('f-vet-loc-feed-dose')}
                variant="outlined"
                sx={styles.formControl}
                value={dose}
                size="small"
                type="number"
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setDose(newValue);
                  }
                }}
              />

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="gilts-only-confirm"
                      color="primary"
                      checked={giltsConfirmed}
                      onChange={(e) => setGiltsConfirmed(e.target.checked)}
                    />
                  }
                  label={t('f-loc-vaccination-gilts-only')}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      {!fullScreen && (
        <DialogActions sx={styles.dialogHeaderFooter}>
          <Button
            onClick={() => closeDialog(null)}
            variant="outlined"
            startIcon={<Icon>{'close'}</Icon>}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={() => closeDialog('ok')}
            id="saveVaccinationBtn"
            startIcon={<FontAwesomeIcon icon={faSyringe} />}
            variant="contained"
            color="primary"
            disabled={submitDisabled}
          >
            {t('f-loc-vaccination-action')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default VeterinaryLocationVaccinationModal;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) => {
  const fieldMaxWidth = 220;
  return {
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: fieldMaxWidth
    },
    dialogHeaderFooter: {
      background: colors.defaultGrey
    },
    dialog: {
      overflow: 'hidden'
    },
    updateForm: {
      minWidth: 250,
      justifyContent: 'center'
    },
    mt2: {
      marginTop: theme.spacing(1)
    },
    infoCardAvatar: {
      background: theme.palette.primary.main
    }
  } as const;
};
